import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import classNames from 'classnames'
import { Document, CloseAlt, Spinner, Exclamation } from '@/components/svg'
import { resolveErrorMessage, ErrorObject } from '@/utils'
import Button from '../Button'
import ButtonBase from '../ButtonBase'
import Popover from '../Popover/Popover'
import Text from '../Text/Text'
import { DropzoneFile } from '../form/types'

interface Props extends DropzoneFile {
  className?: string
}

const FormDropzoneFile: React.FC<Props> = ({
  id,
  name,
  file,
  handleUpload,
  handleRemove,
  className,
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<JSX.Element | string | null>(null)
  const ranOnce = useRef<boolean>(false)

  const parts = name.split('.')
  const extension = parts[parts.length - 1]
  const filename = parts.slice(0, parts.length - 1).join('.')

  const handleUploadStatefully = useCallback(async () => {
    if (!handleUpload) {
      return
    }

    setLoading(true)
    setError(null)
    try {
      await handleUpload()
    } catch (e) {
      setError(resolveErrorMessage(e as ErrorObject))
    } finally {
      setLoading(false)
    }
  }, [handleUpload])

  const handleRemoveStatefully = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!handleRemove) {
        return
      }

      e.preventDefault()
      setLoading(true)
      setError(null)
      try {
        await handleRemove()
      } catch (error) {
        setLoading(false)
        setError(resolveErrorMessage(error as ErrorObject))
      }
    },
    [handleRemove]
  )

  // If an upload handler is passed, and file not already uploaded,
  // then upload the file as soon as the component renders
  useEffect(() => {
    if (!id && !ranOnce.current) {
      ranOnce.current = true
      handleUploadStatefully()
    }
  }, [file, loading, id, handleUploadStatefully])

  const action = useMemo(() => {
    if (loading) {
      return (
        <div className="relative z-20 ml-auto p-1">
          <Spinner className="h-4 w-4 animate-spin-slow text-gray-4" />
        </div>
      )
    }

    if (error) {
      const content = (
        <div className="flex flex-col items-center p-4">
          <Text as="p" preset="body.sm" className="text-white">
            {error}
          </Text>
          <Button
            color="oxide"
            size="sm"
            onClick={id ? handleRemoveStatefully : handleUploadStatefully}
            className="mt-2 w-full"
            type="button"
          >
            Retry
          </Button>
          {!id && (
            <Button
              color="gray"
              size="sm"
              onClick={handleRemoveStatefully}
              className="mt-2 w-full"
              type="button"
            >
              Remove
            </Button>
          )}
        </div>
      )

      return (
        <Popover
          content={content}
          className="relative z-20 ml-auto block p-1"
          label="File Error"
        >
          <Exclamation className="h-4 w-4 text-red" />
        </Popover>
      )
    }

    if (handleRemove) {
      return (
        <ButtonBase
          onClick={handleRemoveStatefully}
          className="relative z-20 ml-auto p-2"
          aria-label="Remove File"
          type="button"
        >
          <CloseAlt className="h-2 w-2 text-gray-4" />
        </ButtonBase>
      )
    }

    return null
  }, [
    loading,
    error,
    id,
    handleRemove,
    handleUploadStatefully,
    handleRemoveStatefully,
  ])

  return (
    <div
      key={name}
      className={classNames(
        {
          'relative flex items-center rounded border bg-white p-1 pl-7': true,
          'border-gray-8': !error,
          'border-red': error,
          'pointer-events-none cursor-default opacity-50': loading,
        },
        className
      )}
    >
      <Document
        className={classNames({
          'absolute left-[3px] top-1 block h-6 w-6': true,
          'text-oxide': !error,
          'text-red': error,
        })}
      />

      <Text as="span" preset="body.sm" className="truncate">
        {`${filename}.`}
      </Text>

      <Text as="span" preset="body.sm">
        {extension}
      </Text>

      {action}
    </div>
  )
}

export default FormDropzoneFile
